/**
 * Public-app Account page (U-37).
 *
 * Renders a full-screen overlay above the map showing the signed-in user's
 * profile, favorites, and alerts. Alerts can be paused (kept on file but
 * skipped by the notifications cron) or removed (hard delete).
 *
 * The overlay is mounted into #account-page and toggled visible by the
 * router's onAccountRoute handler. Closing it (header back link or popstate)
 * navigates back to '/'.
 */

import type { DataClient, AccountPayload, AccountAlert, AccountFavorite } from '@slowmaui/api-client';
import { deleteCurrentUser, signOut } from '@slowmaui/auth';

let dataClient: DataClient | null = null;
let isVisible = false;

export function init(client: DataClient): void {
  dataClient = client;
  ensureContainer();
}

export async function show(): Promise<void> {
  ensureContainer();
  const root = document.getElementById('account-page')!;
  root.style.display = 'block';
  isVisible = true;
  document.body.classList.add('account-mode');
  await renderLoading();
  await loadAndRender();
}

export function hide(): void {
  const root = document.getElementById('account-page');
  if (root) root.style.display = 'none';
  isVisible = false;
  document.body.classList.remove('account-mode');
}

function ensureContainer(): void {
  if (document.getElementById('account-page')) return;
  const div = document.createElement('div');
  div.id = 'account-page';
  div.style.cssText = `
    display: none;
    position: fixed;
    inset: 0;
    z-index: 5000;
    background: #f9fafb;
    overflow-y: auto;
  `;
  document.body.appendChild(div);
}

async function renderLoading(): Promise<void> {
  document.getElementById('account-page')!.innerHTML = `
    <div style="max-width: 720px; margin: 60px auto; padding: 24px; text-align: center; color: #6b7280;">
      Loading…
    </div>
  `;
}

async function loadAndRender(): Promise<void> {
  if (!dataClient || !isVisible) return;
  const root = document.getElementById('account-page')!;

  // If the user isn't signed in, the request will throw an auth error —
  // catch it and show a sign-in prompt instead of crashing.
  let payload: AccountPayload | null = null;
  try {
    payload = await dataClient.getAccount();
  } catch (err) {
    console.warn('[account] getAccount failed (likely not signed in):', err);
  }

  if (!payload) {
    root.innerHTML = renderShell(`
      <section class="account-card">
        <h2>Sign in to manage your account</h2>
        <p>Your favorites and alerts live here once you sign in. Click the
        account icon in the header to get started.</p>
      </section>
    `);
    wireBack();
    return;
  }

  root.innerHTML = renderShell(`
    <section class="account-card">
      <h2>Account</h2>
      <div class="account-profile">
        ${payload.user.photoUrl ? `<img class="account-avatar" src="${escAttr(payload.user.photoUrl)}" alt="">` : ''}
        <div>
          <div class="account-name">${esc(payload.user.name || payload.user.email)}</div>
          <div class="account-email">${esc(payload.user.email)}</div>
        </div>
      </div>
    </section>

    <section class="account-card" id="alerts">
      <h3>Alerts (${payload.alerts.length})</h3>
      ${renderAlerts(payload.alerts)}
    </section>

    <section class="account-card">
      <h3>Favorites (${payload.favorites.length})</h3>
      ${renderFavorites(payload.favorites)}
    </section>

    <section class="account-card account-danger">
      <h3>Delete account</h3>
      <p>
        Permanently removes your profile, favorites, and alerts, and severs your
        sign-in. This can't be undone. Agents you've contacted will lose any
        notes or history tied to your account.
      </p>
      <button class="account-btn account-btn-danger" id="account-delete-open">
        Delete my account
      </button>
    </section>
  `);
  wireBack();
  wireAlertActions();
  wireDeleteAccount();
}

function renderShell(inner: string): string {
  return `
    <div class="account-shell">
      <header class="account-header">
        <a href="/" class="account-back" id="account-back">← Back to map</a>
      </header>
      <main class="account-main">
        ${inner}
      </main>
    </div>
    <style>
      .account-shell { max-width: 720px; margin: 0 auto; padding: 16px; }
      .account-header { padding: 8px 0 16px; }
      .account-back { color: #2c5530; text-decoration: none; font-size: 0.938rem; }
      .account-back:hover { text-decoration: underline; }
      .account-card { background: white; border-radius: 12px; padding: 20px 24px; margin-bottom: 16px; box-shadow: 0 1px 3px rgba(0,0,0,0.08); }
      .account-card h2 { font-size: 1.5rem; color: #1f2937; margin: 0 0 16px; }
      .account-card h3 { font-size: 1.125rem; color: #2c5530; margin: 0 0 12px; padding-bottom: 8px; border-bottom: 1px solid #e5e7eb; }
      .account-card p { color: #4b5563; line-height: 1.6; }
      .account-profile { display: flex; gap: 16px; align-items: center; }
      .account-avatar { width: 56px; height: 56px; border-radius: 50%; object-fit: cover; }
      .account-name { font-weight: 600; color: #1f2937; }
      .account-email { color: #6b7280; font-size: 0.875rem; }
      .account-list { list-style: none; padding: 0; margin: 0; }
      .account-row { display: flex; justify-content: space-between; align-items: center; padding: 12px 0; border-bottom: 1px solid #f3f4f6; gap: 12px; flex-wrap: wrap; }
      .account-row:last-child { border-bottom: 0; }
      .account-row-info { flex: 1; min-width: 200px; }
      .account-row-name a { color: #2c5530; text-decoration: none; font-weight: 500; }
      .account-row-name a:hover { text-decoration: underline; }
      .account-row-meta { color: #6b7280; font-size: 0.813rem; margin-top: 2px; }
      .account-row-actions { display: flex; gap: 6px; }
      .account-btn { font-size: 0.813rem; padding: 6px 12px; border: 1px solid #d1d5db; background: white; color: #374151; border-radius: 6px; cursor: pointer; }
      .account-btn:hover { background: #f9fafb; }
      .account-btn[data-action="resume"] { color: #059669; border-color: #059669; }
      .account-btn[data-action="pause"]  { color: #b45309; border-color: #d97706; }
      .account-btn[data-action="remove"] { color: #dc2626; border-color: #dc2626; }
      .account-btn[disabled] { opacity: 0.5; cursor: wait; }
      .account-paused-pill { font-size: 0.75rem; background: #fef3c7; color: #92400e; padding: 2px 8px; border-radius: 999px; margin-left: 8px; }
      .account-empty { color: #9ca3af; font-style: italic; padding: 8px 0; }
      .account-danger { border: 1px solid #fecaca; }
      .account-danger h3 { color: #b91c1c; border-bottom-color: #fecaca; }
      .account-btn-danger { color: white; background: #dc2626; border-color: #dc2626; padding: 8px 16px; font-size: 0.875rem; }
      .account-btn-danger:hover { background: #b91c1c; }
      .account-modal-backdrop { position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 6000; display: flex; align-items: center; justify-content: center; padding: 16px; }
      .account-modal { background: white; border-radius: 12px; padding: 24px; max-width: 460px; width: 100%; box-shadow: 0 10px 30px rgba(0,0,0,0.2); }
      .account-modal h3 { margin: 0 0 12px; font-size: 1.125rem; color: #b91c1c; }
      .account-modal p { color: #4b5563; line-height: 1.5; margin: 0 0 12px; }
      .account-modal label { display: block; font-size: 0.813rem; color: #374151; margin-top: 12px; margin-bottom: 4px; }
      .account-modal input { width: 100%; padding: 8px 10px; border: 1px solid #d1d5db; border-radius: 6px; font-family: inherit; font-size: 0.938rem; box-sizing: border-box; }
      .account-modal-actions { display: flex; gap: 8px; justify-content: flex-end; margin-top: 16px; }
      .account-modal-error { color: #b91c1c; font-size: 0.813rem; margin-top: 8px; }
      body.account-mode #map-container, body.account-mode .header, body.account-mode .unified-drawer-wrapper { display: none !important; }
    </style>
  `;
}

function renderAlerts(items: AccountAlert[]): string {
  if (items.length === 0) {
    return `<div class="account-empty">No alerts yet. Subscribe to a property or neighborhood from the map.</div>`;
  }
  return `<ul class="account-list">${items.map(a => `
    <li class="account-row" data-item-id="${escAttr(a.itemId)}" data-item-type="${a.itemType}">
      <div class="account-row-info">
        <div class="account-row-name">${itemLink(a)}${a.pausedAt ? '<span class="account-paused-pill">Paused</span>' : ''}</div>
        <div class="account-row-meta">${itemMeta(a)}</div>
      </div>
      <div class="account-row-actions">
        ${a.pausedAt
          ? `<button class="account-btn" data-action="resume">Resume</button>`
          : `<button class="account-btn" data-action="pause">Pause</button>`}
        <button class="account-btn" data-action="remove">Unsubscribe</button>
      </div>
    </li>
  `).join('')}</ul>`;
}

function renderFavorites(items: AccountFavorite[]): string {
  if (items.length === 0) {
    return `<div class="account-empty">No favorites yet.</div>`;
  }
  return `<ul class="account-list">${items.map(f => `
    <li class="account-row">
      <div class="account-row-info">
        <div class="account-row-name">${itemLink(f)}</div>
        <div class="account-row-meta">${itemMeta(f)}</div>
      </div>
    </li>
  `).join('')}</ul>`;
}

function itemLink(item: AccountFavorite): string {
  if (item.itemType === 'neighborhood' && item.slug) {
    return `<a href="/?n=${encodeURIComponent(item.slug)}">${esc(item.name)}</a>`;
  }
  if (item.itemType === 'district' && item.slug) {
    return `<a href="/?d=${encodeURIComponent(item.slug)}">${esc(item.name)}</a>`;
  }
  if (item.itemType === 'region' && item.slug) {
    return `<a href="/?r=${encodeURIComponent(item.slug)}">${esc(item.name)}</a>`;
  }
  if (item.itemType === 'property' && item.listingId) {
    return `<a href="/?p=${encodeURIComponent(item.listingId)}">${esc(item.name)}</a>`;
  }
  return esc(item.name);
}

const ITEM_TYPE_LABELS: Record<AccountFavorite['itemType'], string> = {
  property: 'Property',
  neighborhood: 'Neighborhood',
  district: 'District',
  region: 'Region',
};

function itemMeta(item: AccountFavorite): string {
  const bits: string[] = [ITEM_TYPE_LABELS[item.itemType]];
  if (item.itemType === 'property' && item.neighborhoodName) bits.push(esc(item.neighborhoodName));
  if (item.itemType === 'property' && item.listPrice) bits.push('$' + Math.round(item.listPrice).toLocaleString());
  return bits.join(' · ');
}

function wireBack(): void {
  const back = document.getElementById('account-back');
  back?.addEventListener('click', (e) => {
    e.preventDefault();
    history.pushState(null, '', '/');
    hide();
    // Let the rest of the app handle the home route (re-show map, etc.).
    window.dispatchEvent(new PopStateEvent('popstate'));
  });
}

function wireAlertActions(): void {
  document.querySelectorAll<HTMLButtonElement>('#account-page .account-btn').forEach(btn => {
    btn.addEventListener('click', async () => {
      const row = btn.closest<HTMLElement>('.account-row');
      if (!row || !dataClient) return;
      const itemId = row.dataset.itemId!;
      const action = btn.dataset.action!;
      btn.disabled = true;
      try {
        if (action === 'pause') await dataClient.pauseAlert(itemId);
        else if (action === 'resume') await dataClient.resumeAlert(itemId);
        else if (action === 'remove') await dataClient.removeAlert(itemId);
        await loadAndRender();
      } catch (err) {
        console.error(`[account] ${action} failed:`, err);
        alert(`Failed to ${action}.`);
      } finally {
        btn.disabled = false;
      }
    });
  });
}

function wireDeleteAccount(): void {
  const openBtn = document.getElementById('account-delete-open');
  openBtn?.addEventListener('click', () => openDeleteModal());
}

function openDeleteModal(): void {
  // Tear down any prior backdrop so repeated opens don't stack.
  document.getElementById('account-delete-backdrop')?.remove();

  const backdrop = document.createElement('div');
  backdrop.id = 'account-delete-backdrop';
  backdrop.className = 'account-modal-backdrop';
  backdrop.innerHTML = `
    <div class="account-modal" role="dialog" aria-labelledby="account-delete-title">
      <h3 id="account-delete-title">Delete account?</h3>
      <p>This permanently removes your profile, favorites, and alerts. You'll be signed out. This can't be undone.</p>
      <label for="account-delete-confirm">Type <strong>DELETE</strong> to confirm:</label>
      <input id="account-delete-confirm" type="text" autocomplete="off" autocapitalize="characters" />
      <div class="account-modal-error" id="account-delete-error" style="display: none;"></div>
      <div class="account-modal-actions">
        <button class="account-btn" id="account-delete-cancel">Cancel</button>
        <button class="account-btn account-btn-danger" id="account-delete-confirm-btn" disabled>Delete account</button>
      </div>
    </div>
  `;
  document.body.appendChild(backdrop);

  const input = backdrop.querySelector<HTMLInputElement>('#account-delete-confirm')!;
  const confirmBtn = backdrop.querySelector<HTMLButtonElement>('#account-delete-confirm-btn')!;
  const cancelBtn = backdrop.querySelector<HTMLButtonElement>('#account-delete-cancel')!;
  const errorEl = backdrop.querySelector<HTMLDivElement>('#account-delete-error')!;

  input.addEventListener('input', () => {
    confirmBtn.disabled = input.value.trim() !== 'DELETE';
  });
  cancelBtn.addEventListener('click', () => backdrop.remove());
  backdrop.addEventListener('click', (e) => {
    if (e.target === backdrop) backdrop.remove();
  });

  confirmBtn.addEventListener('click', async () => {
    if (!dataClient) return;
    confirmBtn.disabled = true;
    cancelBtn.disabled = true;
    confirmBtn.textContent = 'Deleting…';
    errorEl.style.display = 'none';
    try {
      // Wipe D1 first — once Firebase Auth is gone we can't issue an ID token.
      await dataClient.deleteAccount();
      // Best-effort Firebase Auth deletion. If the user signed in too long ago
      // Firebase throws auth/requires-recent-login; we surface that and ask
      // them to re-sign-in to finish removing their auth record.
      try {
        await deleteCurrentUser();
      } catch (err) {
        const code = (err as { code?: string })?.code;
        if (code === 'auth/requires-recent-login') {
          errorEl.textContent = 'Your account data was removed. Please sign in again to fully delete your sign-in.';
          errorEl.style.display = 'block';
          await signOut();
          setTimeout(() => {
            backdrop.remove();
            history.pushState(null, '', '/');
            hide();
            window.dispatchEvent(new PopStateEvent('popstate'));
          }, 2500);
          return;
        }
        console.warn('[account] Firebase deleteUser failed (non-fatal):', err);
      }
      await signOut();
      backdrop.remove();
      history.pushState(null, '', '/');
      hide();
      window.dispatchEvent(new PopStateEvent('popstate'));
    } catch (err) {
      console.error('[account] deleteAccount failed:', err);
      errorEl.textContent = 'Failed to delete account. Please try again or contact support.';
      errorEl.style.display = 'block';
      confirmBtn.disabled = false;
      cancelBtn.disabled = false;
      confirmBtn.textContent = 'Delete account';
    }
  });

  setTimeout(() => input.focus(), 50);
}

function esc(s: string | null | undefined): string {
  if (s == null) return '';
  return String(s).replace(/[&<>"']/g, ch => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]!
  ));
}
function escAttr(s: string): string { return esc(s); }
